import React from 'react';
import './MarketplaceTile.css';

export type MarketplaceTileVariant = 'available' | 'empty' | 'disabled';

export interface MarketplaceTileProps {
  /** Lucide-icon node (or any ReactNode) rendered in the leading icon chip. */
  icon: React.ReactNode;
  /** Tile heading. */
  title: string;
  /** Optional second line under the title (description / category scope). */
  scope?: string;
  /** Optional trailing line — count badge ("142 Dokumente") or CTA ("Öffnen →"). */
  footer?: React.ReactNode;
  /**
   * If provided, the tile renders as an `<a>` so native middle-click /
   * cmd-click open in a new tab. Host pages typically `preventDefault`
   * inside `onClick` and drive navigation through their own router state.
   */
  href?: string;
  /**
   * Click handler. Receives the underlying `MouseEvent` so the host can
   * `preventDefault()` and route navigation through `setSearchParams` /
   * `navigate(...)`. Suppressed when `variant='disabled'`.
   */
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /**
   * Resting visual treatment.
   * - `available`: indigo hover-lights-up; the canonical marketplace tile.
   * - `empty`: dashed border + `#fafafa` background; reads as "no items yet".
   *   Still clickable (host decides what drilling in shows).
   * - `disabled`: muted icon palette, `aria-disabled="true"`, click suppressed.
   *   Mirrors the /apps in-development tile pattern. **Should not be
   *   combined with `href`**: ARIA `aria-disabled` does not remove an
   *   anchor from focus order or block native keyboard navigation, so
   *   the disabled-but-href combination would still be Enter-navigable.
   *   Pass no `href` to render a non-tabbable `<div>` instead.
   */
  variant?: MarketplaceTileVariant;
  /** Native `title` attribute — used by `/apps` to surface developmentNote on hover. */
  hoverTitle?: string;
  /** Verbatim `data-testid`. Load-bearing for /documents existing tests. */
  testId?: string;
  /**
   * Compact ("list") layout (#1893). Renders a dense horizontal row —
   * icon + title (+ optional {@link compactMeta}) only. The multi-line
   * `scope` subtitle and the `footer` node are BOTH dropped so the row
   * never nests interactive CTAs inside the clickable tile. The a11y
   * contracts are unchanged: `<a>` only when `href`, non-tabbable `<div>`
   * for `variant='disabled'`. Defaults to `false` (the card layout), so
   * every existing caller is untouched.
   */
  compact?: boolean;
  /**
   * Optional right-aligned meta shown only in {@link compact} mode — the
   * scannable single-line count/status (e.g. "142 Dokumente",
   * "12 Einträge", "Konzeptphase"). Kept distinct from `footer` so pages
   * pass a plain string for the compact row without dragging the card
   * footer's interactive CTA node into it. Ignored in card mode.
   */
  compactMeta?: React.ReactNode;
}

export const MarketplaceTile: React.FC<MarketplaceTileProps> = ({
  icon,
  title,
  scope,
  footer,
  href,
  onClick,
  variant = 'available',
  hoverTitle,
  testId,
  compact = false,
  compactMeta,
}) => {
  const isDisabled = variant === 'disabled';

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isDisabled) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  const className = [
    'marketplace-tile',
    `marketplace-tile--${variant}`,
    compact ? 'marketplace-tile--compact' : '',
  ].filter(Boolean).join(' ');

  const iconClassName = [
    'marketplace-tile__icon',
    isDisabled ? 'marketplace-tile__icon--muted' : '',
  ].filter(Boolean).join(' ');

  // Compact ("list") row: icon + title (+ optional right-aligned meta).
  // The `scope` subtitle and the `footer` node are both intentionally
  // dropped — the footer often carries interactive CTAs (/apps), and a
  // compact row must not nest a button/anchor inside the clickable tile.
  const content = compact ? (
    <>
      <div className={iconClassName}>{icon}</div>
      <h2 className="marketplace-tile__title">{title}</h2>
      {compactMeta != null && (
        <span className="marketplace-tile__meta">{compactMeta}</span>
      )}
    </>
  ) : (
    <>
      <div className={iconClassName}>{icon}</div>
      <div className="marketplace-tile__content">
        <h2 className="marketplace-tile__title">{title}</h2>
        {scope && <p className="marketplace-tile__scope">{scope}</p>}
      </div>
      {footer && <span className="marketplace-tile__footer">{footer}</span>}
    </>
  );

  const commonProps = {
    className,
    title: hoverTitle,
    onClick: handleClick,
    'data-testid': testId,
    ...(isDisabled ? { 'aria-disabled': true as const } : {}),
  };

  if (href) {
    return (
      <a href={href} {...commonProps}>
        {content}
      </a>
    );
  }

  return <div {...commonProps}>{content}</div>;
};

export default MarketplaceTile;
